import { sentenceCase } from 'change-case';
import { useTranslation } from 'react-i18next';

import { useEffect, useRef, useState } from 'react';
// material
import {
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
// components
import Label from '../../components/Label';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead } from '../../sections/@dashboard/user';
// mock
import { useLoadingContext } from '../../context/loadingContext';
import CustomerListToolbar from '../../sections/@dashboard/customer/CustomerListToolbar';
import CustomerMoreMenu from '../../sections/@dashboard/customer/CustomerMoreMenu';
import { CustomerService } from '../../Services/customer.service';

const TABLE_HEAD = [
  { id: 'name', label: 'Họ và tên', label_en: 'Full name', alignRight: false },
  { id: 'phone', label: 'Số điện thoại', label_en: 'Phone', alignRight: false },
  { id: 'email', label: 'Email', label_en: 'Email', alignRight: false },
  { id: 'status', label: 'Trạng thái', label_en: 'Status', alignRight: false },
  { id: '' },
];

const Customer = () => {
  const [page, setPage] = useState(0);

  const [totalUser, setTotalUser] = useState(0);

  const [pageSize, setPageSize] = useState(5);

  const [search, setSearch] = useState('');

  const typingTimeoutRef = useRef();

  const { t } = useTranslation();
  const [customer, setCustomer] = useState([]);

  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');

  const { enqueueSnackbar } = useSnackbar();
  const { handleStartLoading, handleStopLoading } = useLoadingContext();

  useEffect(() => {
    (async () => {
      try {
        handleStartLoading();
        const { data, pagination } = await CustomerService.GetCustomer({ page: page + 1, pageSize, search });
        console.log({ data, pagination });
        setCustomer(data);
        setTotalUser(pagination.count);
      } catch (error) {
        enqueueSnackbar(error.response.data, { variant: 'error' });
      } finally {
        handleStopLoading();
      }
    })();
  }, [page, pageSize, search]);

  const handleSearchChange = (e) => {
    const value = e.target.value;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 500);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(event.target.value);
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * pageSize - customer.length) : 0;

  const isUserNotFound = customer.length === 0;

  const handleResetPassword = (phoneNumber, email) => {
    handleStartLoading();
    CustomerService.ResetCustomer({ phone_number: phoneNumber, email })
      .then((res) => {
        setOpen(true);
        setUrl(res);
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
      .finally(handleStopLoading);
  };

  const handleCopyClick = () => {
    navigator.clipboard.writeText(url).then(() => enqueueSnackbar('Copy thành công', { variant: 'success' }));
  };

  return (
    <>
      <Page title="Customer">
        <Dialog open={open} fullWidth>
          <DialogTitle>Cấp lại mật khẩu</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{url}</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              color="error"
              onClick={() => {
                setOpen(false);
                setUrl('');
              }}
            >
              Hủy
            </Button>
            <Button onClick={handleCopyClick}>Copy</Button>
          </DialogActions>
        </Dialog>
        <Container>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Typography variant="h4" gutterBottom>
              {t('customer.title')}
            </Typography>
          </Stack>

          <Card>
            <CustomerListToolbar onFilterName={handleSearchChange} />
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead headLabel={TABLE_HEAD} />
                  <TableBody>
                    {customer &&
                      customer?.map((row, index) => {
                        return (
                          <TableRow hover key={row._id} tabIndex={-1} role="checkbox">
                            <TableCell width={60} align="center">
                              {index + 1}
                            </TableCell>

                            <TableCell align="left">{row?.name}</TableCell>
                            <TableCell align="left">{row.phone_number}</TableCell>
                            <TableCell align="left">{row.email}</TableCell>
                            <TableCell align="left">
                              <Label variant="ghost" color={!row.isValidate ? 'error' : 'success'}>
                                {sentenceCase(row.isValidate ? 'Valid' : 'invalid')}
                              </Label>
                            </TableCell>

                            <TableCell align="right">
                              <CustomerMoreMenu
                                id={row._id}
                                item={row}
                                status={row.status}
                                onResetPassword={() => handleResetPassword(row.phone_number, row.email)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>

                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={search} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalUser}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </Container>
      </Page>
    </>
  );
};

export default Customer;
